import { styled } from '@linaria/react';
import { useContext, useMemo, useState } from 'react';
import { useFloraPages } from '../../api/flora-pages-client/flora-pages-client.hooks';
import { UserContext } from '../../contexts';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { Icon } from '../../ui-library/icon/icon';
import { Loader } from '../../ui-library/loader/loader';
import { Text } from '../../ui-library/typography/typography';
import { AgentViewHeader } from '../flora-agent/components/agent-view-header';
import { FloraPagesList } from './components/flora-pages-list/flora-pages-list';
import { FloraPagesTable } from './components/flora-pages-table/flora-pages-table';
import { FloraPagesViewToggle, type FloraPagesView } from './components/flora-pages-view-toggle';

/**
 * Gallery of Flora-published pages, rendered as a Flora agent view (inside
 * FloraAgentLayout's main area, like Commands/Automations). Lists ONLY the
 * pages the current user owns — pages shared to the organization by others are
 * intentionally excluded here (they remain reachable via their direct link).
 * Pages are created exclusively by Flora in chat — there is deliberately no
 * create button. Supports a grid/list toggle mirroring the Documents view.
 *
 * @returns {JSX.Element} The Pages gallery view.
 */
export function FloraPages() {
  // Priority 1 so this outranks the `Flora - Bloomfilter` fallback claimed by
  // FloraAgentLayout, which renders this view through its outlet.
  useDocumentTitle('Pages - Flora - Bloomfilter', 1);
  const { floraPages, query } = useFloraPages();
  const { user } = useContext(UserContext);
  const [view, setView] = useState<FloraPagesView>('grid');

  // Show only the current user's own pages. The endpoint also returns pages
  // shared to the organization by others; those are filtered out here so the
  // gallery is strictly "my pages".
  const ownedPages = useMemo(
    () => floraPages.filter((page) => !!user?.id && page.user_id === user.id),
    [floraPages, user?.id],
  );

  // The query is disabled (fetchStatus 'idle') until an organization id is
  // available — that also reports isLoading:true, so without this split a
  // user who lands before global state hydrates sees a spinner with no
  // fetch ever in flight, and no way out.
  const isWaitingForOrganization = query.isLoading && query.fetchStatus === 'idle';
  const isLoading = query.isLoading && query.fetchStatus !== 'idle';
  const showEmptyState = !isLoading && !isWaitingForOrganization && !query.isError && ownedPages.length === 0;
  const hasPages = !isLoading && !query.isError && ownedPages.length > 0;

  return (
    <Container>
      <MainColumn>
        <AgentViewHeader
          title="Pages"
          subtitle="Standalone pages Flora has published for you."
          rightSlot={hasPages ? <FloraPagesViewToggle value={view} onChange={setView} /> : undefined}
        />
        {isLoading && (
          <LoadingWrapper>
            <Loader />
          </LoadingWrapper>
        )}
        {isWaitingForOrganization && (
          <EmptyState>
            <Text size="regular" color="var(--ink-lighter)">
              Waiting for your organization to load…
            </Text>
          </EmptyState>
        )}
        {query.isError && (
          <EmptyState>
            <Icon name="error_outline" color="var(--ink-lighter)" size={48} />
            <Text size="regular" color="var(--ink-lighter)">
              Couldn't load your pages.
            </Text>
            <RetryButton type="button" onClick={() => query.refetch()}>
              Try again
            </RetryButton>
          </EmptyState>
        )}
        {showEmptyState && (
          <EmptyState>
            <Icon name="article" color="var(--ink-lighter)" size={48} />
            <Text size="regular" color="var(--ink-lighter)">
              No pages yet — ask Flora to publish one, e.g. “publish a page summarizing this sprint”.
            </Text>
          </EmptyState>
        )}
        {hasPages && (
          <Content>
            {view === 'grid' ? <FloraPagesList pages={ownedPages} /> : <FloraPagesTable pages={ownedPages} />}
          </Content>
        )}
      </MainColumn>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  padding: 40px 0;
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 80px;
`;

const RetryButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: var(--secondary-base);
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  gap: 12px;
  padding: 0 80px;
`;
